import { Decimal } from "decimal.js";
import { DateTime } from "luxon";
import { getMigratedLeningdeel } from "./leningdeel-migration.js";
import { moveField } from "./object-helpers.js";
import { toSemVer } from "./semver.js";
import { asCur, fuzzyDateParse, generateCustomerId, isKeyOf } from "./utils.js";
export const migrations = [
    {
        version: toSemVer("0.1.2"),
        up: (data) => {
            moveField(["finacieringsOpzet"], ["financieringsOpzet"], data);
            moveField(["klantgegevens", "situatie"], ["financieringsOpzet", "situatie"], data, (val) => {
                switch (val) {
                    case "Doorstromer":
                        return "Doorstromen";
                    case "Omzetter":
                        return "Omzetten";
                    default:
                        return "Koop";
                }
            });
            moveField(["klantgegevens", "percentageOvb"], ["financieringsOpzet", "percentageOvb"], data, (val) => {
                if (typeof val === "string") {
                    return `${val}%`;
                }
                return val;
            });
            return data;
        },
    },
    {
        version: toSemVer("0.1.4"),
        up: (data) => {
            if (data.leningdelen?.values.delen) {
                const migratedNewLoanparts = [];
                for (const loanpart of data.leningdelen.values.delen) {
                    const newLoanpart = getMigratedLeningdeel(loanpart);
                    migratedNewLoanparts.push(newLoanpart);
                }
                data.leningdelen.values.delen = migratedNewLoanparts;
            }
            return data;
        },
    },
    {
        version: toSemVer("0.1.5"),
        up: (data) => {
            moveField(["klantgegevens", "nhg"], ["financieringsOpzet", "useNHG"], data);
            moveField(["financieringsOpzet", "aankoopkosten", "nhg"], ["financieringsOpzet", "aankoopkosten", "nhgHoogte"], data);
            return data;
        },
    },
    {
        version: toSemVer("0.1.6"),
        up: (data) => {
            data.financieringsOpzet = replaceKeys(data.financieringsOpzet);
            if (data.financieringsOpzet) {
                data.financieringsOpzet.benodigdeFinanciering = replaceKeys(data.financieringsOpzet.benodigdeFinanciering);
                data.financieringsOpzet.aankoopkosten = replaceKeys(data.financieringsOpzet.aankoopkosten);
            }
            return data;
            function replaceKeys(obj) {
                if (!obj) {
                    return;
                }
                const keys = Object.keys(obj);
                for (const oldKey of keys) {
                    const newKey = camelCase(oldKey);
                    if (oldKey === newKey) {
                        continue;
                    }
                    obj[newKey] = obj[oldKey];
                    delete obj[oldKey];
                }
                return obj;
            }
            function camelCase(str) {
                return str.replace(/-([a-z])/g, (g) => {
                    return g[1]?.toUpperCase() ?? "";
                });
            }
        },
    },
    {
        version: toSemVer("0.1.7"),
        up: (data) => {
            moveField(["financieringsOpzet", "aankoopkosten", "starterslening"], ["financieringsOpzet", "middelen", "starterslening"], data);
            return data;
        },
    },
    {
        version: toSemVer("0.1.8"),
        up: (data) => {
            moveField(["klantgegevens", "objectEnergielabel"], ["klantgegevens", "objectEnergielabel"], data, (val) => {
                if (val === "TEMP A++++ energieprestatie") {
                    return "14 A++++ (EP garantie)";
                }
                return val;
            });
            moveField(["klantgegevens", "jaarFinLastPercentages"], ["klantgegevens", "passeerdatum"], data, (val) => {
                const now = DateTime.now();
                const date = now.year === val
                    ? now
                    : DateTime.fromObject({ year: Number(val) }, { zone: "Europe/Amsterdam" });
                return date.toFormat("d-M-yyyy");
            });
            if (data.financieringsOpzet?.aankoopkosten) {
                // This is the same logic as the logic in useFinancieringsOpzet.ts, but we copy it here so we can potentially run it on old data without having to load in the vue runtime.
                const gebruiktBankgarantie = data.financieringsOpzet.gebruiktBankgarantie;
                const bankgarantieComputed = data.financieringsOpzet.aankoopkosten.bankgarantie;
                const bankgarantieManual = data.financieringsOpzet.aankoopkosten.bankgarantieManual;
                if (gebruiktBankgarantie === "Ja (handmatig)" &&
                    !bankgarantieManual &&
                    bankgarantieComputed) {
                    data.financieringsOpzet.aankoopkosten.bankgarantieManual =
                        bankgarantieComputed;
                }
                if (gebruiktBankgarantie !== "Ja (handmatig)" && bankgarantieManual) {
                    data.financieringsOpzet.aankoopkosten.bankgarantieManual = undefined;
                }
                if (gebruiktBankgarantie !== "Ja (berekend)" && bankgarantieComputed) {
                    data.financieringsOpzet.aankoopkosten.bankgarantie = undefined;
                }
            }
            return data;
        },
    },
    {
        version: toSemVer("0.2.0"),
        up: (data) => {
            const geldverstrekker = data.leningdelen?.extraInput?.verstrekker;
            if (geldverstrekker) {
                const lenderMapping = {
                    ABN: "AA ABN AMRO Bank N.V.",
                    ABNAMRO: "AA ABN AMRO Bank N.V.",
                    "ABN-AMRO": "AA ABN AMRO Bank N.V.",
                    "ABN-AMRO Budget": "AA ABN AMRO Bank N.V.",
                    "abn meeneem regeling": "AA ABN AMRO Bank N.V.",
                    Aegon: "AE AEGON Verzekeringen",
                    "Aegon Combinatiehypotheek": "AE AEGON Verzekeringen",
                    "AEGON OHA": "AE AEGON Verzekeringen",
                    Allienz: "AZ Allianz",
                    argenta: "AR Argenta",
                    Argenta: "AR Argenta",
                    "Argenta (omzetting) + ABN-AMRO": "AA ABN AMRO Bank N.V.",
                    "ASN Hypotheek": "AK ASN Bank",
                    ASR: "AU ASR",
                    "a.s.r. DigiThuis": "AU ASR",
                    "ASR-Hypotheken": "AU ASR",
                    Attens: "AQ Attens Hypotheken",
                    BijBouwe: "BB BijBouwe",
                    "bij bouwe": "BB BijBouwe",
                    BLG: "BL BLG Hypotheken",
                    "BLG Wonen": "BL BLG Hypotheken",
                    Bunq: "BQ bunq",
                    "Bunq Easy Mortgage": "BQ bunq",
                    "bunq rente 10 jaar vast": "BQ bunq",
                    "Centraal Beheer": "CB Centraal Beheer",
                    "Dynamic Credits": "DY Dynamic",
                    "Dynamic credits": "DY Dynamic",
                    Florius: "FH Florius",
                    HollandWoont: "HV HollandWoont",
                    Hypotrust: "HT Hypotrust",
                    "Hypotrust Profijt": "HT Hypotrust",
                    "Hypotrust Vrij Leven": "HT Hypotrust",
                    "Hypotrust WB": "HT Hypotrust",
                    "hypotrust woonbewust": "HT Hypotrust",
                    ING: "IN ING Bank",
                    ing: "IN ING Bank",
                    "ING Bank": "IN ING Bank",
                    "IN ING": "IN ING Bank",
                    Lloyds: "LL Lloyds Bank",
                    lloyds: "LL Lloyds Bank",
                    "Lloyd's": "LL Lloyds Bank",
                    "Lloyd's Bank": "LL Lloyds Bank",
                    "Lloyd's Bank (bv)": "LL Lloyds Bank",
                    "Lot Hypotheken": "LT Lot Hypotheken",
                    "lot hypotheken": "LT Lot Hypotheken",
                    Merius: "ME Merius",
                    Munt: "MT Munt Hypotheken",
                    "Munt Hypotheken": "MT Munt Hypotheken",
                    "MUNT meeneem": "MT Munt Hypotheken",
                    "Munt (Meenemen)": "MT Munt Hypotheken",
                    "Munt Variabel": "MT Munt Hypotheken",
                    "Nationale Nederlanden": "NN Nationale Nederlanden Hypotheken",
                    "Nationale-Nederlanden Reguliere Hypotheek": "NN Nationale Nederlanden Hypotheken",
                    "Nationalle Nederlanden": "NN Nationale Nederlanden Hypotheken",
                    "Natonale Nederlanden": "NN Nationale Nederlanden Hypotheken",
                    "Neo 10 jaar rentevast": "NX N26 Hypotheken",
                    "Neo Hypotheken": "NX N26 Hypotheken",
                    NIBC: "NI NIBC Direct Hypotheken B.V.",
                    nibc: "NI NIBC Direct Hypotheken B.V.",
                    "NIBC direct": "NI NIBC Direct Hypotheken B.V.",
                    "NIBC Hypotheek": "NI NIBC Direct Hypotheken B.V.",
                    "NIBC Direct Investeringshypotheek": "NI NIBC Direct Hypotheken B.V.",
                    "NIBC OHA": "NI NIBC Direct Hypotheken B.V.",
                    Obvion: "OV Obvion",
                    "obvion 5 jaar vast": "OV Obvion",
                    "Obvion OHA": "OV Obvion",
                    rabobank: "RB Rabo-hypotheekbank N.V.",
                    Rabobank: "RB Rabo-hypotheekbank N.V.",
                    "Rabobank basis Hypotheek": "RB Rabo-hypotheekbank N.V.",
                    "Rabobank Plus": "RB Rabo-hypotheekbank N.V.",
                    "Rabobank Plusvoorwaarden": "RB Rabo-hypotheekbank N.V.",
                    "Rabo BasisHypotheek": "RB Rabo-hypotheekbank N.V.",
                    Robuust: "RF Robuust Hypotheken",
                    "Syntrus Achmea Basishypotheek": "SY Syntrus Achmea Hypotheken",
                    Tulp: "TH Tulp Hypotheken",
                    "Tulp Hypotheken": "TH Tulp Hypotheken",
                    "Tulp-hypotheken": "TH Tulp Hypotheken",
                    "Tulp Riant Hypotheek": "TH Tulp Hypotheken",
                    Venn: "VE Venn Hypotheken",
                    "Venn Hypotheek": "VE Venn Hypotheken",
                    "Venn Hypotheken": "VE Venn Hypotheken",
                    "VISTA Hypotheken": "VA Vista Hypotheken",
                    Woonfonds: "WF Woonfonds Hypotheken",
                };
                data.leningdelen.extraInput.verstrekker = isKeyOf(lenderMapping, geldverstrekker)
                    ? lenderMapping[geldverstrekker]
                    : geldverstrekker;
            }
            return data;
        },
    },
    {
        version: toSemVer("0.2.1"),
        up: (data) => {
            // Set default nhgInputType to 'auto' for existing data models that don't have this field
            if (data.financieringsOpzet?.useNHG === "Ja" &&
                !data.financieringsOpzet.nhgInputType) {
                data.financieringsOpzet.nhgInputType = "auto";
            }
            return data;
        },
    },
    {
        version: toSemVer("0.2.2"),
        up: (data) => {
            // Migrate income fields from klantgegevens to inventarisation data
            if (data.klantgegevens) {
                const inkomenKlant = asCur(data.klantgegevens.inkomenKlant);
                const inkomenPartner = asCur(data.klantgegevens.inkomenPartner);
                // Check if we have any income data to migrate
                const hasClientIncome = inkomenKlant && typeof inkomenKlant === "number" && inkomenKlant > 0;
                const hasPartnerIncome = inkomenPartner &&
                    typeof inkomenPartner === "number" &&
                    inkomenPartner > 0;
                if (hasClientIncome || hasPartnerIncome) {
                    // Ensure customers array exists
                    if (!data.customers || !Array.isArray(data.customers)) {
                        data.customers = [];
                    }
                    // Ensure income structure exists
                    if (!data.income) {
                        data.income = { byCustomer: [] };
                    }
                    if (!data.income.byCustomer) {
                        data.income.byCustomer = [];
                    }
                    // Helper function to get or create a customer
                    const getOrCreateCustomer = (customerIndex) => {
                        // Check if customer already exists
                        const existingCustomer = data.customers[customerIndex];
                        if (existingCustomer) {
                            return existingCustomer;
                        }
                        // Create new customer if it doesn't exist
                        const newCustomer = {
                            id: generateCustomerId(),
                            personDetails: {},
                            contactDetails: {},
                            otherDetails: {},
                            residence: {},
                            identification: {},
                        };
                        data.customers.push(newCustomer);
                        return newCustomer;
                    };
                    // Helper function to create income entry for a customer
                    const createIncomeEntry = (customerId, income) => {
                        // Find existing income entry for this customer
                        let customerIncome = data.income.byCustomer.find((entry) => entry.customerId === customerId);
                        // Create new income entry if it doesn't exist
                        if (!customerIncome) {
                            customerIncome = {
                                customerId,
                                sections: {
                                    loondienst: [],
                                    uitkering: [],
                                    pensioen: [],
                                    onderneming: [],
                                    overig: [],
                                },
                            };
                            data.income.byCustomer.push(customerIncome);
                        }
                        // Add a generic income entry (using "overig" category for migrated data)
                        customerIncome.sections.overig.push({
                            id: globalThis.crypto.randomUUID(),
                            sectionKey: "overig",
                            values: {
                                source: "99 Anders",
                                description: "Gemigreerd inkomen uit klantgegevens",
                                endDate: "",
                                grossYearlyPayment: income,
                                percentageCounted: 100,
                            },
                        });
                    };
                    // Migrate client income (customer 1)
                    if (hasClientIncome) {
                        const customer1 = getOrCreateCustomer(0);
                        createIncomeEntry(customer1.id, inkomenKlant);
                    }
                    // Migrate partner income (customer 2)
                    if (hasPartnerIncome) {
                        const customer2 = getOrCreateCustomer(1);
                        createIncomeEntry(customer2.id, inkomenPartner);
                    }
                }
                // Remove the old income fields if they exist
                if ("inkomenKlant" in data.klantgegevens) {
                    delete data.klantgegevens.inkomenKlant;
                }
                if ("inkomenPartner" in data.klantgegevens) {
                    delete data.klantgegevens.inkomenPartner;
                }
            }
            return data;
        },
    },
    {
        version: toSemVer("0.2.3"),
        up: (data) => {
            data.objectData ??= {};
            data.objectData.address ??= {};
            moveField(["klantgegevens", "adresHuisnummer"], ["objectData", "address", "huisnummer"], data);
            moveField(["klantgegevens", "adresPlaats"], ["objectData", "address", "woonplaatsNaam"], data);
            moveField(["klantgegevens", "adresStraat"], ["objectData", "address", "openbareRuimteNaam"], data);
            // Set korteNaam to the same value as openbareRuimteNaam
            if (data.objectData?.address?.openbareRuimteNaam) {
                data.objectData.address.korteNaam =
                    data.objectData.address.openbareRuimteNaam;
            }
            moveField(["klantgegevens", "adresPostcode"], ["objectData", "address", "postcode"], data);
            moveField(["klantgegevens", "adresToevoeging"], ["objectData", "address", "huisletter"], data);
            if (data.objectData.address &&
                Object.keys(data.objectData.address).length === 0) {
                delete data.objectData.address;
            }
            moveField(["klantgegevens", "marktwaarde"], ["objectData", "marktwaarde"], data);
            moveField(["klantgegevens", "marktwaardeVoorVerbouwing"], ["objectData", "marktwaardeVoorVerbouwing"], data);
            moveField(["klantgegevens", "wozWaarde"], ["objectData", "wozWaarde"], data);
            moveField(["klantgegevens", "objectEnergielabel"], ["objectData", "energielabel"], data);
            moveField(["klantgegevens", "bouwjaar"], ["objectData", "bouwjaar"], data);
            moveField(["klantgegevens", "erfpachtPerMaand"], ["objectData", "erfpacht", "canonYearly"], data, (val) => {
                return (asCur(val) ?? 0) * 12;
            });
            return data;
        },
    },
    {
        version: toSemVer("0.2.4"),
        up: (data) => {
            const obj = {
                ...data,
                financieringsOpzet: data.financieringsOpzet
                    ? {
                        ...data.financieringsOpzet,
                        aankoopkosten: data.financieringsOpzet.aankoopkosten
                            ? Object.entries(data.financieringsOpzet.aankoopkosten).reduce((red, [key, value]) => {
                                // @ts-expect-error
                                red[key] = asCur(value);
                                return red;
                            }, {})
                            : undefined,
                        benodigdeFinanciering: data.financieringsOpzet
                            .benodigdeFinanciering
                            ? Object.entries(data.financieringsOpzet.benodigdeFinanciering).reduce((red, [key, value]) => {
                                // @ts-expect-error
                                red[key] = asCur(value);
                                return red;
                            }, {})
                            : undefined,
                    }
                    : {},
                klantgegevens: data.klantgegevens
                    ? {
                        ...data.klantgegevens,
                        alimentatiePerMaand: asCur(data.klantgegevens.alimentatiePerMaand),
                        bkrVermelding: asCur(data.klantgegevens.bkrVermelding),
                        // @ts-expect-error
                        inkomenKlant: asCur(data.klantgegevens.inkomenKlant),
                        // @ts-expect-error
                        inkomenPartner: asCur(data.klantgegevens.inkomenPartner),
                        restschuld: asCur(data.klantgegevens.restschuld),
                        // @ts-expect-error
                        waardeWoning: asCur(data.klantgegevens.waardeWoning),
                    }
                    : {},
                // @ts-expect-error
                rentepercentages: data.rentepercentages?.rentePercentages
                    ? {
                        // @ts-expect-error
                        ...data.rentepercentages.rentePercentages,
                        marktwaarde: asCur(
                        // @ts-expect-error
                        data.rentepercentages.rentePercentages?.marktwaarde),
                    }
                    : {},
                // @ts-expect-error
                leningdelen: data.leningdelen
                    ? {
                        // @ts-expect-error
                        ...data.leningdelen,
                        values: {
                            // @ts-expect-error
                            ...data.leningdelen.values,
                            // @ts-expect-error
                            delen: data.leningdelen.values?.delen.map((lening) => {
                                return {
                                    ...lening,
                                    toetslastPerMaand: asCur(lening.toetslastPerMaand),
                                    hoogte: asCur(lening.hoogte),
                                    hoogteBox3: asCur(lening.hoogteBox3),
                                };
                            }),
                        },
                    }
                    : {},
                assets: {
                    byCustomer: data.assets?.byCustomer.map((customer) => {
                        return {
                            ...customer,
                            sections: {
                                ...customer.sections,
                                other: customer.sections.other?.map((section) => {
                                    return {
                                        ...section,
                                        values: {
                                            ...section.values,
                                            currentBalance: asCur(section.values?.currentBalance),
                                            monthlyDeposit: asCur(section.values?.monthlyDeposit),
                                        },
                                    };
                                }),
                                savingsAccount: customer.sections.savingsAccount?.map((section) => {
                                    return {
                                        ...section,
                                        values: {
                                            ...section.values,
                                            currentBalance: asCur(section.values?.currentBalance),
                                            monthlyDeposit: asCur(section.values?.monthlyDeposit),
                                        },
                                    };
                                }),
                                investmentAccount: customer.sections.investmentAccount?.map((section) => {
                                    return {
                                        ...section,
                                        values: {
                                            ...section.values,
                                            currentBalance: asCur(section.values?.currentBalance),
                                            monthlyDeposit: asCur(section.values?.monthlyDeposit),
                                        },
                                    };
                                }),
                                savingsPlan: customer.sections.savingsPlan?.map((section) => {
                                    return {
                                        ...section,
                                        values: {
                                            ...section.values,
                                            currentBalance: asCur(section.values?.currentBalance),
                                        },
                                    };
                                }),
                            },
                        };
                    }),
                },
                income: {
                    byCustomer: data.income?.byCustomer.map((incomeCustomer) => {
                        return {
                            ...incomeCustomer,
                            sections: {
                                ...incomeCustomer.sections,
                                loondienst: incomeCustomer.sections.loondienst?.map((loondienst) => {
                                    return {
                                        ...loondienst,
                                        values: loondienst.values
                                            ? {
                                                ...loondienst.values,
                                                uwvToetsinkomen: asCur(loondienst.values.uwvToetsinkomen),
                                                monthlyPensionContribution: asCur(loondienst.values.monthlyPensionContribution),
                                                vehicleCatalogValue: asCur(loondienst.values.vehicleCatalogValue),
                                                vehicleOwnContribution: asCur(loondienst.values.vehicleOwnContribution),
                                                grossSalary: asCur(loondienst.values.grossSalary),
                                                employeePensionContribution: asCur(loondienst.values.employeePensionContribution),
                                                employeeAOPremium: asCur(loondienst.values.employeeAOPremium),
                                                vacationAllowance: asCur(loondienst.values.vacationAllowance),
                                                thirteenthMonth: asCur(loondienst.values.thirteenthMonth),
                                                overtime: asCur(loondienst.values.overtime),
                                                irregularHoursAllowance: asCur(loondienst.values.irregularHoursAllowance),
                                                commission: asCur(loondienst.values.commission),
                                                yearEndBonus: asCur(loondienst.values.yearEndBonus),
                                            }
                                            : undefined,
                                    };
                                }),
                            },
                        };
                    }),
                },
                objectData: data.objectData
                    ? {
                        ...data.objectData,
                        marktwaarde: asCur(data.objectData.marktwaarde),
                        marktwaardeVoorVerbouwing: asCur(data.objectData.marktwaardeVoorVerbouwing),
                        waardeWoning: asCur(data.objectData.waardeWoning),
                        wozWaarde: asCur(data.objectData.wozWaarde),
                        erfpacht: data.objectData.erfpacht
                            ? {
                                ...data.objectData.erfpacht,
                                canonYearly: asCur(data.objectData.erfpacht.canonYearly),
                            }
                            : undefined,
                    }
                    : {},
                liabilities: {
                    byCustomer: data.liabilities?.byCustomer?.map((liability) => {
                        return {
                            ...liability,
                            sections: liability.sections
                                ? {
                                    ...liability.sections,
                                    overdraftFacility: liability.sections.overdraftFacility?.map((section) => {
                                        return {
                                            ...section,
                                            values: {
                                                ...section.values,
                                                creditLimit: asCur(section.values?.creditLimit),
                                                currentOverdraft: asCur(section.values?.currentOverdraft),
                                                monthlyAmount: asCur(section.values?.monthlyAmount),
                                            },
                                        };
                                    }),
                                    revolvingCredit: liability.sections.revolvingCredit?.map((section) => {
                                        return {
                                            ...section,
                                            values: {
                                                ...section.values,
                                                creditLimit: asCur(section.values?.creditLimit),
                                                currentBalance: asCur(section.values?.currentBalance),
                                                monthlyAmount: asCur(section.values?.monthlyAmount),
                                            },
                                        };
                                    }),
                                    personalLoan: liability.sections.personalLoan?.map((section) => {
                                        return {
                                            ...section,
                                            values: {
                                                ...section.values,
                                                principalAmount: asCur(section.values?.principalAmount),
                                                currentBalance: asCur(section.values?.currentBalance),
                                                monthlyAmount: asCur(section.values?.monthlyAmount),
                                            },
                                        };
                                    }),
                                    privateLease: liability.sections.privateLease?.map((section) => {
                                        return {
                                            ...section,
                                            values: {
                                                ...section.values,
                                                monthlyAmount: asCur(section.values?.monthlyAmount),
                                            },
                                        };
                                    }),
                                    studentDebtSF15: liability.sections.studentDebtSF15?.map((section) => {
                                        return {
                                            ...section,
                                            values: {
                                                ...section.values,
                                                originalDebt: asCur(section.values?.originalDebt),
                                                currentDebt: asCur(section.values?.currentDebt),
                                                fixedMonthlyAmount: asCur(section.values?.fixedMonthlyAmount),
                                            },
                                        };
                                    }),
                                    studentDebtSF35: liability.sections.studentDebtSF35?.map((section) => {
                                        return {
                                            ...section,
                                            values: {
                                                ...section.values,
                                                currentDebt: asCur(section.values?.currentDebt),
                                                fixedMonthlyAmount: asCur(section.values?.fixedMonthlyAmount),
                                            },
                                        };
                                    }),
                                    spousalAlimony: liability.sections.spousalAlimony?.map((section) => {
                                        return {
                                            ...section,
                                            values: {
                                                ...section.values,
                                                annualAmount: asCur(section.values?.annualAmount),
                                            },
                                        };
                                    }),
                                }
                                : {},
                        };
                    }) ?? [],
                },
            };
            return obj;
        },
    },
    {
        version: toSemVer("0.2.5"),
        up: (data) => {
            // @ts-expect-error
            const passeerdatum = fuzzyDateParse(data.klantgegevens?.passeerdatum);
            const objectEnergyLabelDate = fuzzyDateParse(data.objectData?.energielabelAfgifteDate);
            const objectWozDate = fuzzyDateParse(data.objectData?.wozWaardeTaxatieDate);
            const erfpachtAfgekochtTot = fuzzyDateParse(data.objectData?.erfpacht?.afgekochtTot);
            const erfpachtEndDate = fuzzyDateParse(data.objectData?.erfpacht?.endDate);
            function mapLeningdeel(lening) {
                if (!lening) {
                    return;
                }
                const rente = asCur(lening.rente);
                // @ts-expect-error - toetsrente exists in legacy data
                const toetsrente = asCur(lening.toetsrente);
                return {
                    ...lening,
                    hoogte: asCur(lening.hoogte),
                    hoogteBox3: asCur(lening.hoogteBox3),
                    looptijd: asCur(lening.looptijd),
                    rentevast: asCur(lening.rentevast),
                    rente: rente !== undefined
                        ? new Decimal(rente).div(100).toNumber()
                        : undefined,
                    // @ts-expect-error - toetsrente exists in legacy data
                    toetsrente: toetsrente !== undefined
                        ? new Decimal(toetsrente).div(100).toNumber()
                        : undefined,
                };
            }
            const obj = {
                ...data,
                financieringsOpzet: data.financieringsOpzet
                    ? {
                        ...data.financieringsOpzet,
                        percentageOvb: (() => {
                            const v = data.financieringsOpzet?.percentageOvb;
                            if (v === "Handmatig")
                                return "manual";
                            if (typeof v === "number")
                                return new Decimal(v).div(100).toNumber();
                            if (typeof v === "string" && v.trim() !== "") {
                                const n = Number(v.replaceAll("%", "").replaceAll(",", "."));
                                return Number.isFinite(n)
                                    ? new Decimal(n).div(100).toNumber()
                                    : undefined;
                            }
                            return undefined;
                        })(),
                    }
                    : {},
                klantgegevens: data.klantgegevens
                    ? {
                        ...data.klantgegevens,
                        // @ts-expect-error
                        passeerdatum: passeerdatum.isValid
                            ? passeerdatum.toISODate()
                            : undefined,
                    }
                    : {},
                rentepercentages: data.rentepercentages
                    ? {
                        ...data.rentepercentages,
                        loanToValue: data.rentepercentages.loanToValue !== undefined &&
                            data.rentepercentages.loanToValue !== null
                            ? new Decimal(data.rentepercentages.loanToValue)
                                .div(100)
                                .toNumber()
                            : undefined,
                    }
                    : {},
                // @ts-expect-error
                leningdelen: data.leningdelen
                    ? {
                        // @ts-expect-error
                        ...data.leningdelen,
                        // @ts-expect-error
                        values: data.leningdelen.values
                            ? {
                                // @ts-expect-error
                                ...data.leningdelen.values,
                                // @ts-expect-error
                                delen: data.leningdelen.values?.delen?.map(mapLeningdeel),
                                totaaldeel: mapLeningdeel(
                                // @ts-expect-error
                                data.leningdelen.values?.totaaldeel),
                                existingParts: 
                                // @ts-expect-error
                                data.leningdelen.values?.existingParts?.map(mapLeningdeel),
                                newLoanpartsTotal: mapLeningdeel(
                                // @ts-expect-error
                                data.leningdelen.values
                                    ?.newLoanpartsTotal),
                            }
                            : undefined,
                    }
                    : {},
                customers: data.customers?.map((customer) => {
                    const birthDate = fuzzyDateParse(customer.personDetails?.birthDate, {
                        twoDigitYearCentury: "past",
                    });
                    const identificationIssueDate = fuzzyDateParse(customer.identification?.issueDate);
                    const formerMaritalStatusDate = fuzzyDateParse(customer.otherDetails?.formerMaritalStatus?.date);
                    return {
                        ...customer,
                        personDetails: customer.personDetails
                            ? {
                                ...customer.personDetails,
                                birthDate: birthDate.isValid
                                    ? birthDate.toISODate()
                                    : undefined,
                            }
                            : {},
                        identification: customer.identification
                            ? {
                                ...customer.identification,
                                issueDate: identificationIssueDate.isValid
                                    ? identificationIssueDate.toISODate()
                                    : undefined,
                            }
                            : {},
                        otherDetails: customer.otherDetails
                            ? {
                                ...customer.otherDetails,
                                formerMaritalStatus: customer.otherDetails.formerMaritalStatus
                                    ? {
                                        ...customer.otherDetails.formerMaritalStatus,
                                        date: formerMaritalStatusDate.isValid
                                            ? formerMaritalStatusDate.toISODate()
                                            : undefined,
                                    }
                                    : {},
                            }
                            : {},
                    };
                }),
                liabilities: {
                    byCustomer: data.liabilities?.byCustomer?.map((liability) => {
                        return {
                            ...liability,
                            sections: liability.sections
                                ? {
                                    ...liability.sections,
                                    revolvingCredit: liability.sections.revolvingCredit?.map((section) => {
                                        const endDate = fuzzyDateParse(section.values?.endDate);
                                        return {
                                            ...section,
                                            values: {
                                                ...section.values,
                                                endDate: endDate.isValid
                                                    ? endDate.toISODate()
                                                    : undefined,
                                            },
                                        };
                                    }),
                                    personalLoan: liability.sections.personalLoan?.map((section) => {
                                        const endDate = fuzzyDateParse(section.values?.endDate);
                                        return {
                                            ...section,
                                            values: {
                                                ...section.values,
                                                endDate: endDate.isValid
                                                    ? endDate.toISODate()
                                                    : undefined,
                                            },
                                        };
                                    }),
                                    privateLease: liability.sections.privateLease?.map((section) => {
                                        const startDate = fuzzyDateParse(section.values?.startDate);
                                        return {
                                            ...section,
                                            values: {
                                                ...section.values,
                                                startDate: startDate.isValid
                                                    ? startDate.toISODate()
                                                    : undefined,
                                                durationMonths: asCur(section.values?.durationMonths),
                                            },
                                        };
                                    }),
                                    studentDebtSF15: liability.sections.studentDebtSF15?.map((section) => {
                                        const endDate = fuzzyDateParse(section.values?.endDate);
                                        const interestRate = asCur(section.values?.interestRate);
                                        return {
                                            ...section,
                                            values: {
                                                ...section.values,
                                                remainingMonths: asCur(section.values?.remainingMonths),
                                                endDate: endDate.isValid
                                                    ? endDate.toISODate()
                                                    : undefined,
                                                interestRate: interestRate !== undefined
                                                    ? new Decimal(interestRate)
                                                        .div(100)
                                                        .toNumber()
                                                    : undefined,
                                            },
                                        };
                                    }),
                                    studentDebtSF35: liability.sections.studentDebtSF35?.map((section) => {
                                        const endDate = fuzzyDateParse(section.values?.endDate);
                                        const interestRate = asCur(section.values?.interestRate);
                                        return {
                                            ...section,
                                            values: {
                                                ...section.values,
                                                remainingMonths: asCur(section.values?.remainingMonths),
                                                endDate: endDate.isValid
                                                    ? endDate.toISODate()
                                                    : undefined,
                                                interestRate: interestRate !== undefined
                                                    ? new Decimal(interestRate)
                                                        .div(100)
                                                        .toNumber()
                                                    : undefined,
                                            },
                                        };
                                    }),
                                    spousalAlimony: liability.sections.spousalAlimony?.map((section) => {
                                        const endDate = fuzzyDateParse(section.values?.endDate);
                                        return {
                                            ...section,
                                            values: {
                                                ...section.values,
                                                endDate: endDate.isValid
                                                    ? endDate.toISODate()
                                                    : undefined,
                                            },
                                        };
                                    }),
                                }
                                : {},
                        };
                    }) ?? [],
                },
                income: {
                    byCustomer: data.income?.byCustomer?.map((incomeCustomer) => {
                        return {
                            ...incomeCustomer,
                            sections: incomeCustomer.sections
                                ? {
                                    ...incomeCustomer.sections,
                                    loondienst: incomeCustomer.sections.loondienst?.map((loondienst) => {
                                        const startDate = fuzzyDateParse(loondienst.values?.startDate);
                                        const hoursPerWeek = asCur(loondienst.values?.hoursPerWeek);
                                        const percentageForToetsinkomen = asCur(loondienst.values?.percentageForToetsinkomen);
                                        const vehicleAdditionPercentage = asCur(loondienst.values?.vehicleAdditionPercentage);
                                        return {
                                            ...loondienst,
                                            values: loondienst.values
                                                ? {
                                                    ...loondienst.values,
                                                    startDate: startDate.isValid
                                                        ? startDate.toISODate()
                                                        : undefined,
                                                    hoursPerWeek: hoursPerWeek !== undefined
                                                        ? hoursPerWeek
                                                        : undefined,
                                                    percentageForToetsinkomen: percentageForToetsinkomen !== undefined
                                                        ? new Decimal(percentageForToetsinkomen)
                                                            .div(100)
                                                            .toNumber()
                                                        : undefined,
                                                    vehicleAdditionPercentage: vehicleAdditionPercentage !== undefined
                                                        ? new Decimal(vehicleAdditionPercentage)
                                                            .div(100)
                                                            .toNumber()
                                                        : undefined,
                                                }
                                                : undefined,
                                        };
                                    }),
                                    onderneming: incomeCustomer.sections.onderneming?.map((onderneming) => {
                                        const startDate = fuzzyDateParse(onderneming.values?.startDate);
                                        const percentageCounted = asCur(onderneming.values?.percentageCounted);
                                        return {
                                            ...onderneming,
                                            values: onderneming.values
                                                ? {
                                                    ...onderneming.values,
                                                    startDate: startDate.isValid
                                                        ? startDate.toISODate()
                                                        : undefined,
                                                    resultYearOne: asCur(onderneming.values?.resultYearOne),
                                                    resultYearTwo: asCur(onderneming.values?.resultYearTwo),
                                                    resultYearThree: asCur(onderneming.values?.resultYearThree),
                                                    averageResult: asCur(onderneming.values?.averageResult),
                                                    assessedIncome: asCur(onderneming.values?.assessedIncome),
                                                    percentageCounted: percentageCounted !== undefined
                                                        ? new Decimal(percentageCounted)
                                                            .div(100)
                                                            .toNumber()
                                                        : undefined,
                                                }
                                                : undefined,
                                        };
                                    }),
                                    uitkering: incomeCustomer.sections.uitkering?.map((uitkering) => {
                                        const startDate = fuzzyDateParse(uitkering.values?.startDate);
                                        const endDate = fuzzyDateParse(uitkering.values?.endDate);
                                        const percentageCounted = asCur(uitkering.values?.percentageCounted);
                                        return {
                                            ...uitkering,
                                            values: uitkering.values
                                                ? {
                                                    ...uitkering.values,
                                                    startDate: startDate.isValid
                                                        ? startDate.toISODate()
                                                        : undefined,
                                                    endDate: endDate.isValid
                                                        ? endDate.toISODate()
                                                        : undefined,
                                                    grossYearlyBenefit: asCur(uitkering.values?.grossYearlyBenefit),
                                                    vacationBenefit: asCur(uitkering.values?.vacationBenefit),
                                                    percentageCounted: percentageCounted !== undefined
                                                        ? new Decimal(percentageCounted)
                                                            .div(100)
                                                            .toNumber()
                                                        : undefined,
                                                }
                                                : undefined,
                                        };
                                    }),
                                    pensioen: incomeCustomer.sections.pensioen?.map((pensioen) => {
                                        const pensionStartDate = fuzzyDateParse(pensioen.values?.pensionStartDate);
                                        const paymentEndDate = fuzzyDateParse(pensioen.values?.paymentEndDate);
                                        const percentageCounted = asCur(pensioen.values?.percentageCounted);
                                        return {
                                            ...pensioen,
                                            values: pensioen.values
                                                ? {
                                                    ...pensioen.values,
                                                    pensionStartDate: pensionStartDate.isValid
                                                        ? pensionStartDate.toISODate()
                                                        : undefined,
                                                    paymentEndDate: paymentEndDate.isValid
                                                        ? paymentEndDate.toISODate()
                                                        : undefined,
                                                    grossYearlyPayment: asCur(pensioen.values?.grossYearlyPayment),
                                                    percentageCounted: percentageCounted !== undefined
                                                        ? new Decimal(percentageCounted)
                                                            .div(100)
                                                            .toNumber()
                                                        : undefined,
                                                }
                                                : undefined,
                                        };
                                    }),
                                    overig: incomeCustomer.sections.overig?.map((overig) => {
                                        const endDate = fuzzyDateParse(overig.values?.endDate);
                                        const percentageCounted = asCur(overig.values?.percentageCounted);
                                        return {
                                            ...overig,
                                            values: overig.values
                                                ? {
                                                    ...overig.values,
                                                    endDate: endDate.isValid
                                                        ? endDate.toISODate()
                                                        : undefined,
                                                    grossYearlyPayment: asCur(overig.values?.grossYearlyPayment),
                                                    percentageCounted: percentageCounted !== undefined
                                                        ? new Decimal(percentageCounted)
                                                            .div(100)
                                                            .toNumber()
                                                        : undefined,
                                                }
                                                : undefined,
                                        };
                                    }),
                                }
                                : {},
                        };
                    }),
                },
                objectData: data.objectData
                    ? {
                        ...data.objectData,
                        energielabelAfgifteDate: objectEnergyLabelDate.isValid
                            ? objectEnergyLabelDate.toISODate()
                            : undefined,
                        wozWaardeTaxatieDate: objectWozDate.isValid
                            ? objectWozDate.toISODate()
                            : undefined,
                        erfpacht: data.objectData.erfpacht
                            ? {
                                ...data.objectData.erfpacht,
                                afgekochtTot: erfpachtAfgekochtTot.isValid
                                    ? erfpachtAfgekochtTot.toISODate()
                                    : undefined,
                                endDate: erfpachtEndDate.isValid
                                    ? erfpachtEndDate.toISODate()
                                    : undefined,
                            }
                            : undefined,
                    }
                    : {},
            };
            return obj;
        },
    },
    {
        version: toSemVer("0.2.6"),
        up: (data) => {
            // @ts-expect-error
            let passingdate = fuzzyDateParse(data.klantgegevens?.passeerdatum);
            if (!passingdate.isValid) {
                passingdate = DateTime.now();
            }
            // @ts-expect-error
            if (data.leningdelen) {
                // @ts-expect-error
                data.mortgage = data.leningdelen;
                // @ts-expect-error
                delete data.leningdelen;
            }
            // @ts-expect-error
            const { existingParts } = (data.mortgage?.values ?? {});
            // @ts-expect-error
            if (data.mortgage.values?.existingParts) {
                // @ts-expect-error
                delete data.mortgage.values.existingParts;
            }
            // @ts-expect-error
            if (data.mortgage.computed) {
                // @ts-expect-error
                delete data.mortgage.computed;
            }
            data.mortgage.mortgageParts = [
                ...(existingParts?.map((part) => ({
                    ...part,
                    existingMortgagePartId: part.id,
                })) ?? []).flatMap((l) => leningdeelToMortgageParts(l, "existing")),
                // @ts-expect-error
                ...(data.mortgage?.values?.delen ?? []).flatMap((l) => leningdeelToMortgageParts(l, "new")),
            ];
            // @ts-expect-error
            if (data.mortgage?.values) {
                // @ts-expect-error
                delete data.mortgage.values;
            }
            // @ts-expect-error
            data.mortgage.lenderCode = data.mortgage?.extraInput?.verstrekker;
            data.mortgage.productCode =
                // @ts-expect-error
                data.mortgage?.extraInput?.verstrekkerProduct;
            // @ts-expect-error
            if (data.mortgage?.extraInput) {
                // @ts-expect-error
                delete data.mortgage.extraInput;
            }
            function hypotheekvormToRepaymentForm(hypotheekvorm) {
                switch (hypotheekvorm) {
                    case "Annuïteitenhypotheek":
                        return "annuity";
                    case "Lineaire hypotheek":
                        return "linear";
                    case "Aflossingsvrije hypotheek":
                        return "interest-only";
                    case "Bankspaar- of spaarhypotheek":
                        return "savings-mortgage";
                    case "BEW- of beleggingshypotheek":
                        return "investment-mortgage";
                    default:
                        return undefined;
                }
            }
            function leningdeelToMortgageParts(leningdeel, existingOrNew) {
                const repaymentForm = hypotheekvormToRepaymentForm(leningdeel.hypotheekvorm);
                const hoogte = leningdeel.hoogte ?? 0;
                const hoogteBox3 = leningdeel.hoogteBox3 ?? 0;
                const hasBox3 = hoogteBox3 > 0;
                const isFullyBox3 = hasBox3 && hoogteBox3 >= hoogte;
                const shouldSplit = hasBox3 && !isFullyBox3;
                const createBasePart = (id, amount, interestDeductionLaw) => {
                    const part = {
                        id,
                        repaymentForm,
                        amount,
                        interestRate: leningdeel.rente,
                        interestDeductionLaw,
                    };
                    if (existingOrNew === "new") {
                        part.durationMonths = leningdeel.looptijd;
                        part.fixedInterestMonths = leningdeel.rentevast;
                    }
                    else if (existingOrNew === "existing") {
                        part.endDate =
                            leningdeel.looptijd !== undefined
                                ? (passingdate
                                    .plus({ months: leningdeel.looptijd })
                                    .toISODate() ?? undefined)
                                : undefined;
                        part.fixedInterestEndDate =
                            leningdeel.rentevast !== undefined
                                ? (passingdate
                                    .plus({ months: leningdeel.rentevast })
                                    .toISODate() ?? undefined)
                                : undefined;
                        part.existingMortgagePartId = leningdeel.id;
                    }
                    return part;
                };
                if (shouldSplit) {
                    // Split into box1 (deductible) and box3 (non-deductible) parts
                    const box1Amount = hoogte - hoogteBox3;
                    const box3Amount = hoogteBox3;
                    return [
                        createBasePart(globalThis.crypto.randomUUID(), box1Amount, "new"),
                        createBasePart(globalThis.crypto.randomUUID(), box3Amount, "none"),
                    ];
                }
                // Single part - either fully box1 or fully box3
                const interestDeductionLaw = isFullyBox3 ? "none" : "new";
                return [createBasePart(leningdeel.id, hoogte, interestDeductionLaw)];
            }
            function mortgagePartToExistingMortgageParts(mortgage) {
                return {
                    id: mortgage.id,
                    interestRate: mortgage.interestRate,
                    amountOriginal: mortgage.amount,
                    startDate: passingdate.toISODate() ?? undefined,
                    endDate: mortgage.endDate,
                    repaymentForm: mortgage.repaymentForm,
                    fixedInterestEndDate: mortgage.fixedInterestEndDate,
                    interestDeductionLaw: mortgage.interestDeductionLaw,
                };
            }
            const newExistingParts = [];
            for (const mortgagePart of data.mortgage.mortgageParts.filter((p) => p.existingMortgagePartId)) {
                const existingMortgagePart = mortgagePartToExistingMortgageParts(mortgagePart);
                newExistingParts.push(existingMortgagePart);
                mortgagePart.existingMortgagePartId = existingMortgagePart.id;
            }
            const obj = {
                ...data,
                existingMortgages: newExistingParts.length > 0
                    ? [
                        {
                            id: globalThis.crypto.randomUUID(),
                            lenderCode: data.mortgage.lenderCode,
                            productCode: data.mortgage.productCode,
                            nhg: data.financieringsOpzet.useNHG === "Ja",
                            kadasterRanking: 1,
                            mortgageParts: newExistingParts,
                        },
                    ]
                    : [],
            };
            return obj;
        },
    },
    {
        version: toSemVer("0.2.7"),
        up: (data) => {
            moveField(["klantgegevens", "passeerdatum"], ["financieringsOpzet", "passeerdatum"], data);
            data.financieringsOpzet ??= {};
            if (!data.financieringsOpzet?.passeerdatum) {
                data.financieringsOpzet.passeerdatum = DateTime.now()
                    .plus({ months: 2 })
                    .startOf("month")
                    .toISODate();
            }
            if (data.klantgegevens) {
                const validFields = new Set([
                    "alimentatiePerMaand",
                    "bkrVermelding",
                    "restschuld",
                    "waardeWoning",
                ]);
                for (const key of Object.keys(data.klantgegevens)) {
                    if (!validFields.has(key)) {
                        delete data.klantgegevens[key];
                    }
                }
            }
            return data;
        },
    },
];
